import type { Frame, CanvasDoc } from '../store/types';
import { isPanelOwned } from '../store/types';

const DB_NAME = 'codeframe-history';
const FRAMES = 'frameVersions';
const CHECKPOINTS = 'checkpoints';
const VERSION = 1;

export interface FrameVersionMeta { key: number; frameId: string; ts: number; label?: string }
export interface CheckpointMeta { id: string; ts: number; label: string; kind: 'manual' | 'auto-turn' }
interface FrameVersionRecord extends FrameVersionMeta { frame: Frame }
// `seq` is a monotonic insertion counter (internal). It — not `ts` or the random
// `id` suffix — is the authoritative ordering key, so checkpoints created within the
// same millisecond still sort by true insertion order. Recomputed as persisted-max+1
// on each write — O(checkpoints) per put, fine at the ~70-row retention cap; survives
// reload since it derives from stored records.
interface CheckpointRecord extends CheckpointMeta { seq: number; doc: CanvasDoc }

let dbPromise: Promise<IDBDatabase> | null = null;
function openDb(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(FRAMES)) {
        const s = db.createObjectStore(FRAMES, { keyPath: 'key', autoIncrement: true });
        s.createIndex('byFrame', 'frameId', { unique: false });
      }
      if (!db.objectStoreNames.contains(CHECKPOINTS)) {
        db.createObjectStore(CHECKPOINTS, { keyPath: 'id' });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  return dbPromise;
}

function run<T>(store: string, mode: IDBTransactionMode, fn: (s: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  return openDb().then((db) => new Promise<T>((resolve, reject) => {
    const req = fn(db.transaction(store, mode).objectStore(store));
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  }));
}

function getAllByFrame(frameId: string): Promise<FrameVersionRecord[]> {
  return openDb().then((db) => new Promise<FrameVersionRecord[]>((resolve, reject) => {
    const idx = db.transaction(FRAMES, 'readonly').objectStore(FRAMES).index('byFrame');
    const req = idx.getAll(IDBKeyRange.only(frameId));
    req.onsuccess = () => resolve(req.result as FrameVersionRecord[]);
    req.onerror = () => reject(req.error);
  }));
}

export const historyStore = {
  async putFrameVersion(frameId: string, frame: Frame, label?: string): Promise<number> {
    return run<number>(FRAMES, 'readwrite', (s) =>
      s.add({ frameId, ts: Date.now(), label, frame } as FrameVersionRecord) as IDBRequest<number>);
  },
  async listFrameVersions(frameId: string): Promise<FrameVersionMeta[]> {
    const recs = await getAllByFrame(frameId);
    return recs
      .map(({ key, frameId: fid, ts, label }) => ({ key, frameId: fid, ts, label }))
      .sort((a, b) => b.ts - a.ts || b.key - a.key);
  },
  async getFrameVersion(key: number): Promise<Frame | undefined> {
    const rec = await run<FrameVersionRecord | undefined>(FRAMES, 'readonly', (s) => s.get(key));
    return rec?.frame;
  },
  async pruneFrameVersions(frameId: string, keepN: number): Promise<void> {
    const recs = (await getAllByFrame(frameId)).sort((a, b) => b.ts - a.ts || b.key - a.key);
    for (const r of recs.slice(keepN)) await run(FRAMES, 'readwrite', (s) => s.delete(r.key));
  },
  async deleteFrameVersions(frameId: string): Promise<void> {
    const recs = await getAllByFrame(frameId);
    for (const r of recs) await run(FRAMES, 'readwrite', (s) => s.delete(r.key));
  },
  async putCheckpoint(c: { label: string; kind: 'manual' | 'auto-turn'; doc: CanvasDoc }): Promise<string> {
    const existing = await run<CheckpointRecord[]>(CHECKPOINTS, 'readonly', (s) => s.getAll());
    const seq = existing.reduce((m, r) => Math.max(m, r.seq ?? 0), 0) + 1;
    const id = `cp_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    const doc = { ...c.doc, frames: c.doc.frames.filter((f) => !isPanelOwned(f)) };
    await run(CHECKPOINTS, 'readwrite', (s) => s.put({ id, ts: Date.now(), seq, label: c.label, kind: c.kind, doc } as CheckpointRecord));
    return id;
  },
  async listCheckpoints(): Promise<CheckpointMeta[]> {
    const recs = await run<CheckpointRecord[]>(CHECKPOINTS, 'readonly', (s) => s.getAll());
    return recs
      .map(({ id, ts, label, kind, seq }) => ({ id, ts, label, kind, seq }))
      // Newest first by monotonic insertion seq (authoritative). ts is informational
      // only — two checkpoints in the same ms still order deterministically by seq.
      .sort((a, b) => b.seq - a.seq)
      .map(({ id, ts, label, kind }) => ({ id, ts, label, kind }));
  },
  async getCheckpoint(id: string): Promise<CanvasDoc | undefined> {
    const rec = await run<CheckpointRecord | undefined>(CHECKPOINTS, 'readonly', (s) => s.get(id));
    return rec?.doc;
  },
  async deleteCheckpoint(id: string): Promise<void> {
    await run(CHECKPOINTS, 'readwrite', (s) => s.delete(id));
  },
  async pruneCheckpoints(keepAutoTurn: number, keepManual: number): Promise<void> {
    const metas = await this.listCheckpoints(); // newest first
    const seen = { 'auto-turn': 0, manual: 0 } as Record<'auto-turn' | 'manual', number>;
    const keep = { 'auto-turn': keepAutoTurn, manual: keepManual } as Record<'auto-turn' | 'manual', number>;
    for (const m of metas) {
      seen[m.kind] += 1;
      if (seen[m.kind] > keep[m.kind]) await this.deleteCheckpoint(m.id);
    }
  },
  async clearAll(): Promise<void> {
    await run(FRAMES, 'readwrite', (s) => s.clear());
    await run(CHECKPOINTS, 'readwrite', (s) => s.clear());
    // test-only: also drops the cached connection so the next open re-creates a clean DB
    dbPromise = null;
  },
};
